import { styled } from "@mui/system";
import { emphasize } from "@mui/material/styles";

// FormControlRoot component
export const FormControlRoot = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  zIndex: "auto",
  ".iePlaceholder": {
    position: "absolute",
    color: "#86868b",
    top: "16px",
    left: "16px",
    backgroundColor: "transparent",
    opacity: "0.42",
    letterSpacing: "-0.7px"
  },
  "& .warningContainer": {
    fontSize: "12px",
    color: "#ea991d",
    margin: 0,
    marginTop: "10px"
  },
  "& .MuiInputLabel-outlined": { width: "78%" },
  "& .MuiFormLabel-root": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#585A61",
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: "22px",
    letterSpacing: "0.08px",
    transform: "translate(16px, 18.5px) scale(1)"
  },
  "& .MuiInputLabel-shrink, & .MuiFormLabel-filled": {
    fontWeight: 500,
    fontSize: "0.875rem",
    transform: "translate(16px, 10px) scale(0.75)"
  },
  "& .MuiInputBase-root": {
    borderRadius: "10px",
    "& .MuiInputBase-input": {
      display: "flex",
      position: "relative",
      padding: "25px 16px 8px 16px",
      "& > div:first-of-type": {
        padding: "0",
        "& > div:first-of-type": {
          padding: 0,
          marginLeft: 0
        }
      },
      "& > div:last-of-type": {
        position: "absolute",
        width: "56px",
        height: "56px",
        top: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }
    },
    "& fieldset": {
      "& legend": {
        display: "none"
      }
    },
    "&:hover": {
      backgroundColor: "#FFF"
    },
    "&:focus-within": {
      borderColor: "var(--Color-Text-Primary-On-Light, #1F1F1F)"
    }
  },
  "& .MuiOutlinedInput-notchedOutline": {
    top: 0
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--Color-Text-Primary-On-Light, #1F1F1F)",
    borderWidth: "1px !important"
  },
  "& .Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ea2b1e"
  }
});

// Input component
export const Input = styled("div")(({ disabled, error, warning }) => ({
  color: "#2C2C2C",
  display: "flex",
  padding: 0,
  minHeight: "56px",
  height: "auto",
  backgroundColor: disabled ? "#F2F2F2" : "#FFF",
  fontSize: "1rem",
  fontWeight: 400,
  lineHeight: "22px",
  letterSpacing: "0.08px",
  borderRadius: "10px",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: error ? "#ea2b1e" : warning ? "#ea991d" : "#BCBDC0",
  "& > *": {
    minHeight: "56px"
  },
  "& + fieldset": {
    display: "none"
  },
  "&:hover": {
    backgroundColor: "#FFF"
  },
  "&:focus-within": {
    borderColor: "var(--Color-Text-Primary-On-Light, #1F1F1F)"
  }
}));

// ValueContainer component
export const ValueContainer = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  flex: 1,
  alignItems: "center",
  padding: "0 16px"
});

// Chip component
export const Chip = styled("div")(({ theme }) => ({
  margin: `${theme.spacing(0.5)} ${theme.spacing(0.25)}`,
  backgroundColor: "#F2F2F2",
  borderRadius: "4px",
  padding: "2px 6px",
  fontSize: "0.875rem"
}));

// ChipFocused component
export const ChipFocused = styled("div")(({ theme }) => ({
  backgroundColor: emphasize(
    theme.palette.mode === "light" ? theme.palette.grey[300] : theme.palette.grey[700],
    0.08
  )
}));

// NoOptionsMessage component
export const NoOptionsMessage = styled("div")(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  color: "#585A61",
  fontSize: "0.875rem"
}));

// SingleValue component
export const SingleValue = styled("div")({
  fontSize: "1rem",
  fontWeight: 400,
  lineHeight: "22px",
  letterSpacing: "0.08px",
  color: "#2C2C2C"
});

// Placeholder component
export const Placeholder = styled("div")({
  position: "absolute",
  left: 16,
  fontSize: "1rem",
  fontWeight: 400,
  lineHeight: "22px",
  letterSpacing: "0.08px",
  color: "#86868b",
  opacity: 0.42
});

// Divider component
export const Divider = styled("div")(({ theme }) => ({
  height: theme.spacing(2)
}));

// Indicator component
export const Indicator = styled("div")({
  width: "56px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  "& svg": {
    fontSize: "18px",
    color: "#585A61"
  }
});

// IndicatorSeparator component
export const IndicatorSeparator = styled("div")({
  width: 1,
  height: "60%",
  position: "absolute",
  top: "20%",
  backgroundColor: "rgba(194, 194, 194, 0.56)",
  left: 0
});

// MenuItem component
export const MenuItem = styled("div")({
  minHeight: 48,
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
  whiteSpace: "normal",
  lineHeight: "normal",
  padding: "8px 16px",
  fontSize: "1rem",
  fontWeight: 400,
  color: "#2C2C2C",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)"
  },
  "& .MuiCheckbox-root": {
    marginLeft: "auto"
  }
});

// Updated customStyles for react-select to match Input component styling
export const customStyles = {
  control: (base, state) => ({
    ...base,
    height: "56px",
    minHeight: "56px",
    borderRadius: "10px",
    backgroundColor: state.isDisabled ? "#F2F2F2" : "#FFF",
    border: state.isFocused
      ? "1px solid var(--Color-Text-Primary-On-Light, #1F1F1F)"
      : "1px solid #BCBDC0",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#FFF",
      borderColor: state.isFocused ? "var(--Color-Text-Primary-On-Light, #1F1F1F)" : "#BCBDC0"
    }
  }),
  menu: provided => ({
    ...provided,
    zIndex: 50,
    marginTop: "6px",
    width: "100%",
    borderRadius: "10px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)"
  }),
  singleValue: (provided, state) => ({
    ...provided,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    padding: "2px",
    color: "#2C2C2C",
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: "22px",
    letterSpacing: "0.08px",
    marginTop: state.selectProps.labelText ? "6px" : "0px",
    maxWidth: "calc(100% - 40px)" // Leave space for indicators
  }),
  placeholder: provided => ({
    ...provided,
    color: "#86868b",
    opacity: 0.42,
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: "22px",
    letterSpacing: "0.08px"
  }),
  input: provided => ({
    ...provided,
    color: "#2C2C2C",
    fontSize: "1rem",
    fontWeight: 400,
    margin: "0",
    padding: "0",
    lineHeight: "22px",
    letterSpacing: "0.08px"
  }),
  dropdownIndicator: provided => ({
    ...provided,
    color: "#585A61",
    padding: "0 8px",
    "&:hover": {
      color: "#2C2C2C"
    }
  }),
  clearIndicator: provided => ({
    ...provided,
    color: "#585A61",
    padding: "0 8px",
    "&:hover": {
      color: "#2C2C2C"
    }
  }),
  indicatorSeparator: provided => ({
    ...provided,
    backgroundColor: "rgba(194, 194, 194, 0.56)",
    margin: "12px 0"
  }),
  valueContainer: provided => ({
    ...provided,
    padding: "0 16px",
    overflow: "hidden"
  }),
  menuList: provided => ({
    ...provided,
    padding: "4px 0"
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "rgba(0, 0, 0, 0.08)" : "transparent",
    color: "#2C2C2C",
    fontSize: "1rem",
    fontWeight: 400,
    padding: "8px 16px",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)"
    }
  })
};
